// Kırılım hiyerarşisi saf yardımcıları (spec §17/§18) — DB/FE bağımsız, tek kaynak.
import type { KpiBreakdown, KpiNodeKind } from "../schemas/kpi.js";

export interface BreakdownNodeRef {
  id: string;
  parentId: string | null;
  nodeKind?: KpiNodeKind | undefined;
  dimensionMember?: string | null | undefined;
  breakdown?: KpiBreakdown | null | undefined;
  orderIndex: number;
  /** Kardeş sırası ikinci anahtarı (set satırı etiketi = override ?? katalog etiketi). */
  label?: string | undefined;
}

/**
 * Aday grup sırası — deterministik: `orderIndex`, sonra etiket (`compareKpiSiblings` gibi, "tr"),
 * sonra kimlik. Saf yol (set ağacı) ve veri yolu (SQL) aynı orderIndex'li iki tam grupta aynı
 * hesap grubunu seçer.
 */
export function compareCalculationGroups(a: BreakdownNodeRef, b: BreakdownNodeRef): number {
  return (
    a.orderIndex - b.orderIndex ||
    (a.label ?? "").localeCompare(b.label ?? "", "tr") ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

/** Etkin düğüm türü: açık kategori/grup kazanır; metrik + grup dışı çocuk → category (eski veri). */
export function effectiveNodeKind(
  node: BreakdownNodeRef,
  children: readonly BreakdownNodeRef[],
): KpiNodeKind | "total" {
  if (node.nodeKind === "category" || node.nodeKind === "breakdown_group") return node.nodeKind;
  if (children.some((c) => c.nodeKind !== "breakdown_group")) return "category";
  if (children.length > 0) return "total";
  return "metric";
}

/**
 * Doğrulama grubu — TEK kural (AI Kontrolü `breakdown_mismatch`, resolve `breakdownChecks`, FE amber
 * nokta + editör doğrulama satırı): `complete=true ∧ rollup !== "none"`. `none` grubu (oran/%
 * varsayılanı) yalnız gösterimdir — üyelerinin toplamı üstle karşılaştırılmaz (Kadın %10 + Erkek
 * %12 ≠ %11 sahte uyarısı).
 */
export function isVerificationGroup(g: BreakdownNodeRef): boolean {
  return (
    g.nodeKind === "breakdown_group" &&
    g.breakdown?.complete === true &&
    g.breakdown.rollup !== "none"
  );
}

/** Hesap grubu adayı — doğrulama grubunun özel hâli (bugün aynı koşul; hesap grubu da doğrulanır). */
export function isCalculationGroupCandidate(g: BreakdownNodeRef): boolean {
  return isVerificationGroup(g);
}

/**
 * Set satırının hesap grubu kararı — TEK kural (resolve hücreleri + yazıcıların müşteri geneli
 * `effectiveDerived`'i): açık seçim varsa türetilmiş ve grup YALNIZ o (sette yoksa ya da artık aday
 * değilse `null` — ilk adaya düşülmez, kapalı başarısızlık); yoksa NULL kuralı: ≥1 üye satırı olan
 * adaylar arasında `compareCalculationGroups` sırasıyla ilk (üyesiz tam grup türetilmişlik doğurmaz).
 */
export function calculationGroupDecision<T extends BreakdownNodeRef & { memberCount: number }>(
  groups: readonly T[],
  explicitId: string | null,
): { derived: boolean; group: T | null } {
  if (explicitId) {
    const hit = groups.find((g) => g.id === explicitId);
    return { derived: true, group: hit && isCalculationGroupCandidate(hit) ? hit : null };
  }
  const candidates = groups
    .filter((g) => g.memberCount > 0 && isCalculationGroupCandidate(g))
    .sort(compareCalculationGroups);
  const group = candidates[0] ?? null;
  return { derived: group !== null, group };
}

/**
 * Toplamda yalnız hesap grubunun üyeleri (iki tam grup çift sayılmaz); hesap grubu yoksa [].
 * Grup seçimi `calculationGroupDecision` (memberCount = `childrenOf(g.id).length`). Toplam olmayan
 * düğümde doğrudan çocuklar.
 */
export function breakdownTransparentChildren<T extends BreakdownNodeRef>(
  parent: T,
  childrenOf: (id: string) => readonly T[],
  explicitCalcGroupId: string | null,
): readonly T[] {
  const children = childrenOf(parent.id);
  if (effectiveNodeKind(parent, children) !== "total") return children;
  // TODO-KPI-127: resolve ile TEK kural — açık seçim aday değilse ya da NULL kuralında üyesiz tam
  // grup ilk sıradaysa ilk adaya/üyesiz gruba düşülmez (döngü kapısı gizli döngüyü kaçırmaz).
  const { group } = calculationGroupDecision(
    children.map((g) => ({ ...g, memberCount: childrenOf(g.id).length })),
    explicitCalcGroupId,
  );
  return group ? childrenOf(group.id) : [];
}

/** Beklenen üyeler: global (clientId null) ∪ bu müşterinin özel üyeleri; girdi sırası korunur. */
export function expectedMembersFor(
  groupMembers: readonly { id: string; clientId: string | null }[],
  clientId: string,
): string[] {
  return groupMembers
    .filter((m) => m.clientId === null || m.clientId === clientId)
    .map((m) => m.id);
}

/** Satırın etkin türü: `effectiveNodeKind` + kırılımlı toplam (`total`). */
export type KpiRowKind = KpiNodeKind | "total";

/**
 * Düz satır listesi (set ağacı / çözülmüş grid) → satır kimliği başına etkin tür. Çocuklar yalnız
 * listedeki satırlardan kurulur; kırılımsız sette sonuç eski "çocuğu olan = kategori" kuralıyla
 * birebir (rapor kanalları byte-eşit — RK19 / altın kayıt).
 */
export function rowKindsOf<T extends BreakdownNodeRef>(
  rows: readonly T[],
): Map<string, KpiRowKind> {
  const children = new Map<string, T[]>();
  for (const r of rows) {
    if (!r.parentId) continue;
    const list = children.get(r.parentId) ?? [];
    list.push(r);
    children.set(r.parentId, list);
  }
  const out = new Map<string, KpiRowKind>();
  for (const r of rows) out.set(r.id, effectiveNodeKind(r, children.get(r.id) ?? []));
  return out;
}

// ─── Derinlik (spec 2026-11-02 §6 — iki seviyeli kırılım) ───────────────────────────────────────

/**
 * En çok kaç kırılım seviyesi açılır: toplam → grup → üye → grup → üye (TODO-KPI-123/-150). Kapı
 * `breakdown-placement.ts` (`breakdown_depth`); motor ve kanallar derinliği parametrik tutar.
 */
export const KPI_BREAKDOWN_MAX_DEPTH = 2;

/** Ata zinciri okuması için en küçük düğüm görünümü (katalog satırı, set satırı, öneri kataloğu). */
export interface BreakdownAncestryNode {
  parentId: string | null;
  nodeKind?: KpiNodeKind | undefined;
  dimensionMember?: string | null | undefined;
}

type NodeOf = (id: string) => BreakdownAncestryNode | undefined;

/**
 * Kırılım seviyesi: düğümün kendisi dahil ata zincirindeki `breakdown_group` sayısı (toplam/düz
 * gösterge 0; birinci seviye grup ve üyeleri 1; üyenin altındaki grup ve üyeleri 2). Döngüde ya da
 * bilinmeyen düğümde durur (o ana kadar sayılan döner).
 */
export function breakdownLevelOf(id: string, nodeOf: NodeOf): number {
  let level = 0;
  const seen = new Set<string>();
  let cur: string | null = id;
  while (cur !== null && !seen.has(cur)) {
    seen.add(cur);
    const n = nodeOf(cur);
    if (!n) break;
    if (n.nodeKind === "breakdown_group") level += 1;
    cur = n.parentId;
  }
  return level;
}

/**
 * Düğüm kırılım ailesinin yapısal parçası mı: grup, ya da üye = `dimensionMember` dolu VE üstü
 * grup (backend `unitOwnerMetricId` ile aynı üye kuralı).
 */
function isStructuralNode(n: BreakdownAncestryNode, nodeOf: NodeOf): boolean {
  if (n.nodeKind === "breakdown_group") return true;
  if (n.dimensionMember === undefined || n.dimensionMember === null || n.parentId === null) {
    return false;
  }
  return nodeOf(n.parentId)?.nodeKind === "breakdown_group";
}

/**
 * Kırılım ailesinin kökü (en üst toplam): grup/üye → üst, iki seviyede de en üst toplama çıkılır
 * (Mavi yaka › Kadın → Toplam Çalışan). Aile dışı düğüm kendisi. Üyede birim sahibi
 * (`unitOwnerMetricId`) ve paket önerisi hedefi bu köktür; grubun kendisi için `unitOwnerMetricId`
 * grubu döner (grup birim taşımaz, kilit/çevrim üyeden ya da toplamdan gelir). Döngüde ya da kopuk
 * zincirde son okunabilen düğüm döner (çağıran yapısal düğüm geldiyse düşürür).
 */
export function breakdownFamilyRootOf(id: string, nodeOf: NodeOf): string {
  const seen = new Set<string>();
  let cur = id;
  for (;;) {
    if (seen.has(cur)) return cur;
    seen.add(cur);
    const n = nodeOf(cur);
    if (!n || !isStructuralNode(n, nodeOf) || n.parentId === null) return cur;
    if (!nodeOf(n.parentId)) return cur;
    cur = n.parentId;
  }
}
